import { CheckCircle2, Circle, Clock, RotateCcw, FlaskConical, Info } from "lucide-react";
import { motion } from "framer-motion";
import { Switch } from "@/components/ui/switch";

const BUCKETS = [
  { id: "today", label: "TODAY", hint: "Quick wins — do these first." },
  { id: "this_week", label: "THIS WEEK", hint: "Compound trust in 5-7 days." },
  { id: "this_month", label: "THIS MONTH", hint: "Deeper foundation work." },
];

export default function PriorityRoadmap({
  roadmap,
  completedTasks = [],
  onToggleTask,
  narration,
  simulationMode = false,
  simulatedResolved = [],
  // Phase 7 (v1 launch cleanup): the backend's own recompute_projection()
  // already reports which checked findings it could NOT honestly assign a
  // projected point value to (no single truthful target value exists for
  // a continuous/banded signal — see scoring.py's _fixed_value_for). This
  // was already computed and returned by /simulate; it just wasn't shown.
  nonSimulatableIds = [],
  onSimulationToggle,
  onSimulatedToggle,
  onSimulationReset,
  simulating = false,
}) {
  const completedSet = new Set(completedTasks);
  const simulatedSet = new Set(simulatedResolved);
  const nonSimulatableSet = new Set(nonSimulatableIds);
  const recLookup = new Map(
    (narration?.recommendations || []).map((r) => [r.finding_id, r]),
  );

  return (
    <div className="pp-surface" data-testid="priority-roadmap">
      <div className="px-6 py-5 border-b border-[#333C47] flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="pp-label mb-1">Priority Roadmap</div>
          <div className="font-display text-xl text-white">
            What to fix, in what order.
          </div>
        </div>
        <div className="flex items-center gap-4">
          {simulationMode && (
            <button
              type="button"
              onClick={onSimulationReset}
              className="pp-btn-secondary text-xs flex items-center gap-1.5"
              data-testid="simulation-reset"
              disabled={simulating}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset simulation
            </button>
          )}
          <label
            htmlFor="simulation-mode"
            className="flex items-center gap-2.5 cursor-pointer select-none"
            data-testid="simulation-mode-label"
          >
            <FlaskConical
              className="h-3.5 w-3.5"
              style={{ color: simulationMode ? "#6CC24A" : "#C7CBD1" }}
            />
            <span className="pp-label text-[10px]">Simulation mode</span>
            <Switch
              id="simulation-mode"
              checked={simulationMode}
              onCheckedChange={onSimulationToggle}
              data-testid="simulation-mode-toggle"
            />
          </label>
        </div>
      </div>

      {simulationMode && (
        <div
          className="px-6 py-3 border-b border-[#333C47] bg-[#131820] text-xs text-[#C7CBD1] flex items-center gap-2"
          data-testid="simulation-notice"
        >
          <span
            className="inline-block w-1.5 h-1.5 bg-[#6CC24A]"
            style={{ boxShadow: "0 0 8px #6CC24A" }}
          />
          Planning mode — check items to preview a projected score. Nothing is
          saved until you complete a new assessment.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3">
        {BUCKETS.map((b, idx) => {
          const rawItems = roadmap?.[b.id] || [];
          // In simulation mode, sort simulated-resolved items to the bottom
          const items = simulationMode
            ? [...rawItems].sort((a, b2) => {
                const aDone = simulatedSet.has(a.id);
                const bDone = simulatedSet.has(b2.id);
                if (aDone === bDone) return 0;
                return aDone ? 1 : -1;
              })
            : rawItems;

          const doneCount = simulationMode
            ? rawItems.filter((i) => simulatedSet.has(i.id)).length
            : rawItems.filter((i) => completedSet.has(i.id)).length;
          const pct =
            rawItems.length === 0
              ? 0
              : Math.round((doneCount / rawItems.length) * 100);

          return (
            <div
              key={b.id}
              className={`p-5 ${idx < 2 ? "md:border-r border-[#333C47]" : ""}`}
              data-testid={`roadmap-bucket-${b.id}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <div className="pp-label" style={{ color: "#6CC24A" }}>
                  {b.label}
                </div>
                <div className="metric text-xs text-[#C7CBD1] flex items-center gap-2">
                  <span>
                    {doneCount}/{rawItems.length}
                  </span>
                </div>
              </div>
              <div className="text-xs text-[#C7CBD1] mb-3">{b.hint}</div>
              <div className="h-1 bg-[#1A1F24] border border-[#333C47] mb-4">
                <motion.div
                  className="h-full bg-[#6CC24A]"
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
              <ul className="space-y-3">
                {items.length === 0 && (
                  <li className="text-xs text-[#C7CBD1] italic">
                    Nothing critical here right now.
                  </li>
                )}
                {items.map((it) => {
                  const isDone = simulationMode
                    ? simulatedSet.has(it.id)
                    : completedSet.has(it.id);
                  const rec = recLookup.get(it.id);
                  const headline = rec?.headline || it.title;
                  return (
                    <motion.li
                      key={it.id}
                      layout
                      transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
                      className="flex items-start gap-2.5"
                    >
                      <button
                        onClick={() => {
                          if (simulationMode) {
                            onSimulatedToggle?.(it.id);
                          } else {
                            onToggleTask?.(it.id);
                          }
                        }}
                        className="mt-0.5"
                        data-testid={
                          simulationMode
                            ? `sim-toggle-${it.id}`
                            : `task-toggle-${it.id}`
                        }
                        aria-label="Toggle task"
                      >
                        {isDone ? (
                          <CheckCircle2 className="h-4 w-4 text-[#6CC24A]" />
                        ) : (
                          <Circle className="h-4 w-4 text-[#C7CBD1]" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm leading-snug ${
                            isDone ? "text-[#C7CBD1] line-through" : "text-white"
                          }`}
                        >
                          {headline}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {it.impact != null && (
                            <span className="pp-badge-green">+{it.impact} trust</span>
                          )}
                          {simulationMode && isDone && nonSimulatableSet.has(it.id) && (
                            <span
                              className="pp-badge-neutral"
                              title="This action may still matter, but its score impact can't be reliably modeled from the evidence available this run."
                              data-testid={`sim-non-simulatable-${it.id}`}
                            >
                              <Info className="h-3 w-3" /> Impact can't be modeled
                            </span>
                          )}
                          <span className="pp-badge-neutral">
                            <Clock className="h-3 w-3" /> {it.time}
                          </span>
                          <span className="pp-badge-neutral">{it.difficulty}</span>
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
